"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useClarityAuth } from "@/lib/auth/use-clarity-auth";
import { useClaritySession } from "@/lib/auth/auth-store";
import {
  createTenantHref,
  getFirstAccessibleTenantId,
  getValidTenantIds,
} from "@/lib/auth/tenant-access";
import { Spinner } from "@clarityos/ui/spinner";
import ErrorPage from "../layout/statusPages/ErrorPage";
import { getCurrentReturnTo, storeReturnTo } from "@/lib/auth/return-to";

export function AuthenticatedAppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, isLoading, loginWithRedirect, logout } =
    useClarityAuth();
  const session = useClaritySession();
  const workspaceTenantIds = getValidTenantIds(session.tenantIds);
  const firstTenantId = getFirstAccessibleTenantId(session.tenantIds);
  const [hasRedirectError, setHasRedirectError] = useState(false);
  const hasTriggeredLoginRedirect = useRef(false);
  const pathSegments = pathname.split("/").filter(Boolean);
  const isTenantWorkspaceRoute = workspaceTenantIds.includes(
    pathSegments[0] ?? "",
  );

  const handleLogout = () => {
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      hasTriggeredLoginRedirect.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading || isAuthenticated || hasTriggeredLoginRedirect.current) {
      return;
    }

    const returnTo = getCurrentReturnTo();

    hasTriggeredLoginRedirect.current = true;
    storeReturnTo(returnTo);

    void loginWithRedirect({
      appState: { returnTo },
    }).catch(() => {
      hasTriggeredLoginRedirect.current = false;
      setHasRedirectError(true);
    });
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (
      session.status !== "authenticated" ||
      isTenantWorkspaceRoute ||
      !firstTenantId
    ) {
      return;
    }

    router.replace(createTenantHref(firstTenantId));
  }, [firstTenantId, isTenantWorkspaceRoute, router, session.status]);

  if ((!isAuthenticated && hasRedirectError) || session.status === "error") {
    return (
      <ErrorPage
        title="Something went wrong while signing in."
        description={
          "Try refreshing the page, or logging out and back in. Contact your administrator if the issue persists."
        }
        actionButton={{
          children: "Sign Out",
          onClick: handleLogout,
        }}
      />
    );
  }

  if (session.status === "authenticated" && isTenantWorkspaceRoute) {
    return <div className="min-h-screen">{children}</div>;
  }

  if (
    session.status === "authenticated" &&
    !isTenantWorkspaceRoute &&
    !firstTenantId
  ) {
    return (
      <ErrorPage
        title="Practice access not available."
        description="Your logged-in session doesn't have access to the practice. Try logging out and back in. Contact your administrator if the issue persists."
        actionButton={{
          children: "Sign Out",
          onClick: handleLogout,
        }}
      />
    );
  }

  return <Spinner alignment="fixedCentered" />;
}
